import { createStyles, Group, UnstyledButton } from '@mantine/core';
import { FilterTargetLabel } from '@repo/common/components/Filter/FilterTarget/FilterTargetLabel';
import { Icon, Text } from '@repo/foundations';
import { observer } from 'mobx-react-lite';
import { forwardRef, type ForwardedRef } from 'react';
import type { EditBlockStore } from '../../store';
import { requiresEditActionValue } from '../../utils';

const useStyles = createStyles(
	(theme, { hasClear }: { hasClear: boolean }) => ({
		root: {
			border: `1px solid ${theme.other.getColor('border/primary/default')}`,
			backgroundColor: theme.other.getColor('fill/primary/default'),
			borderRadius: theme.radius.sm,
			padding: `0 0 0 ${theme.spacing.sm}`,
			gap: 0,

			button: {
				outline: 'none !important',
			},
		},
		detailedRoot: {
			gap: 0,
		},
		detailedLabel: {
			paddingRight: theme.spacing['2xs'],
		},
		target: {
			backgroundColor: theme.other.getColor('fill/primary/default'),
			padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']}`,
			borderTopRightRadius: !hasClear ? theme.radius.sm : undefined,
			borderBottomRightRadius: !hasClear ? theme.radius.sm : undefined,

			'&:hover, &:focus': {
				backgroundColor: theme.other.getColor('fill/primary/hover'),
			},

			'&:active': {
				backgroundColor: theme.other.getColor('fill/primary/active'),
			},
		},
		detailedValue: {
			fontSize: theme.fontSizes.xs,
			color: theme.other.getColor('text/primary/default'),
			lineHeight: theme.spacing.lg,
		},
		clearButton: {
			padding: `${theme.spacing['5xs']} ${theme.spacing.xs} ${theme.spacing['5xs']} 0`,
			borderTopRightRadius: theme.radius.sm,
			borderBottomRightRadius: theme.radius.sm,
			backgroundColor: theme.other.getColor('fill/primary/default'),
			color: theme.other.getColor('icon/primary/default'),

			'&:hover, &:focus': {
				backgroundColor: theme.other.getColor('fill/primary/hover'),
				color: theme.other.getColor('icon/primary/hover'),
			},

			'&:active': {
				backgroundColor: theme.other.getColor('fill/primary/active'),
				color: theme.other.getColor('icon/primary/active'),
			},
		},
	})
);

export interface EditTargetProps {
	store: EditBlockStore;
	actionSearchKey: string;
	label: string;
	isMenuOpen?: boolean;
	onClear: () => void;
	onToggle: () => void;
}

export const EditTarget = observer(
	forwardRef(
		(
			{
				store,
				actionSearchKey,
				isMenuOpen,
				label,
				onClear,
				onToggle,
			}: EditTargetProps,
			ref: ForwardedRef<HTMLButtonElement>
		) => {
			const { classes } = useStyles({
				hasClear: !!onClear,
			});

			const editAction = store.editActions.get(actionSearchKey)!;
			const hasValue = requiresEditActionValue(editAction.editAction.type);
			const valueLabel = editAction.getValueLabel();

			return (
				<Group data-testid="edit-target" className={classes.root}>
					<Group className={classes.detailedRoot}>
						<FilterTargetLabel
							label={label}
							showChevron={false}
							className={classes.detailedLabel}
						/>
						{hasValue && (
							<UnstyledButton
								ref={ref}
								onClick={onToggle}
								className={classes.target}
							>
								<Group spacing="2xs">
									<Text className={classes.detailedValue}>
										{valueLabel ?? '(no selection)'}
									</Text>
									{!onClear && (
										<Icon
											name={isMenuOpen ? 'chevronUp' : 'chevronDown'}
											iconPadding={0}
										/>
									)}
								</Group>
							</UnstyledButton>
						)}
					</Group>
					{!!onClear && (
						<UnstyledButton
							onClick={onClear}
							className={classes.clearButton}
							data-testid="filter-target-remove"
						>
							<Icon name="x" />
						</UnstyledButton>
					)}
				</Group>
			);
		}
	)
);
EditTarget.displayName = 'EditTarget';
